import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Plus, Edit, Trash2, X, Loader2, Upload, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: '', text: '', location: '', rating: 5, revenue: '', order: 0, imageUrl: '', active: true };

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/admin/reviews`); setReviews(res.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setFile(null); setPreview(null); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name || '', text: r.text || '', location: r.location || '', rating: r.rating || 5, revenue: r.revenue || '', order: r.order || 0, imageUrl: r.image || '', active: r.active !== false }); setFile(null); setPreview(r.image || null); setModal(true); };

  const onFile = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return toast({ title: 'Name and review text are required', variant: 'destructive' });
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name); fd.append('text', form.text); fd.append('location', form.location);
      fd.append('rating', String(form.rating)); fd.append('revenue', form.revenue); fd.append('order', String(form.order));
      fd.append('imageUrl', form.imageUrl || '');
      if (file) fd.append('image', file);
      if (editing) { fd.append('active', String(form.active)); await axios.put(`${API}/admin/reviews/${editing.id}`, fd); }
      else { await axios.post(`${API}/admin/reviews`, fd); }
      toast({ title: editing ? 'Review updated' : 'Review added' });
      setModal(false); load();
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.name}?`)) return;
    try { await axios.delete(`${API}/admin/reviews/${r.id}`); toast({ title: 'Deleted' }); load(); }
    catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><MessageSquare className="w-6 h-6 text-blue-600" /> Client Reviews</h2>
        <Button onClick={openNew} data-testid="add-review-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-2" /> Add Review</Button>
      </div>
      <p className="text-sm text-slate-500">Reviews added here replace the default testimonials on the landing page. Add a client photo/result image for extra trust.</p>

      {loading ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.length === 0 && <div className="text-slate-400 text-sm">No reviews yet. Add your first one.</div>}
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4 relative">
              <div className="flex items-center gap-3 mb-2">
                {r.image ? <img src={r.image} alt={r.name} className="w-11 h-11 rounded-full object-cover border" /> : <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">{r.name?.[0]}</div>}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">{r.name}</p>
                  <p className="text-xs text-slate-400">{r.location} {r.revenue ? `\u2022 ${r.revenue}` : ''}</p>
                </div>
              </div>
              <div className="flex mb-2">{[...Array(r.rating || 5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-yellow-400" fill="#facc15" />)}</div>
              <p className="text-sm text-slate-600 italic line-clamp-3">"{r.text}"</p>
              {r.active === false && <span className="text-[10px] text-red-500 font-semibold">Hidden</span>}
              <div className="flex gap-1 absolute top-2 right-2">
                <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Edit className="w-4 h-4" /></button>
                <button onClick={() => remove(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setModal(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <h3 className="text-lg font-bold text-slate-800 mb-4">{editing ? 'Edit Review' : 'Add Review'}</h3>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client name *" data-testid="review-name" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              <div className="grid grid-cols-2 gap-2">
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
                <input value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} placeholder="Revenue e.g. \u20b950K/mo" className="px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              </div>
              <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Review text *" rows={3} data-testid="review-text" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Rating</label>
                  <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 bg-white">
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} Stars</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Sort order</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Client photo / result image</label>
                <label className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-3 cursor-pointer hover:border-blue-400">
                  <Upload className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-500">{file ? file.name : 'Upload image (or paste URL below)'}</span>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="review-image" />
                </label>
                <input value={form.imageUrl} onChange={(e) => { setForm({ ...form, imageUrl: e.target.value }); setPreview(e.target.value); }} placeholder="or image URL" className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm" />
                {preview && <img src={preview} alt="preview" className="mt-2 w-16 h-16 rounded-full object-cover border" />}
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-blue-600" /> Visible on site</label>
              )}
              <Button onClick={save} disabled={saving} data-testid="review-save" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Review'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
